// External libraries
import type { ParsedQs } from 'qs';
import { parse as parseParams } from 'qs';
// Internal Modules
import { URL_PARAM_REGEX_WHITE_LIST } from './constants';

let params: ParsedQs | null = null;

/**
 * @function getParams
 * @returns {Object} Object with key/value pairs as the query string
 * parameter from the window location
 * @description Util function for making it easier to read query params
 */
export const getParams = (): ParsedQs => {
  if (!params || process.env.NODE_ENV === 'test') {
    const query = window.location.search.slice(1);

    params = parseParams(query);
  }
  return params;
};

interface ParamTypeInput {
  paramType?: keyof typeof URL_PARAM_REGEX_WHITE_LIST;
}

type OverrideParams = Record<string, string | boolean>;
export const getOverrideParams = ({ paramType }: ParamTypeInput = {}):
  | Record<string, string | boolean>
  | undefined => {
  if (!paramType) {
    return;
  }
  const WHITE_LIST_REGEX = URL_PARAM_REGEX_WHITE_LIST[paramType];

  if (!WHITE_LIST_REGEX) {
    return;
  }

  // Remove `?` at the beginning of the search string
  const query = window.location.search.substring(1);

  if (!query.length) {
    return;
  }

  const mapValue = (value: string) => {
    switch (value) {
      case 'true':
        return true;
      case 'false':
        return false;
      default:
        return value;
    }
  };

  return query
    .split('&')
    .filter(param => WHITE_LIST_REGEX.test(param))
    .reduce((overrides: OverrideParams, param) => {
      const [key, value = 'true'] = param.split('=');
      const feature = decodeURIComponent(key.replace(WHITE_LIST_REGEX, ''));
      const featureValue = decodeURIComponent(value);

      // For booleans we don't want their value as a string
      overrides[feature] = mapValue(featureValue);
      return overrides;
    }, {});
};

getParams.reset = () => {
  params = null;
};
